/**
 * Uploads em três tempos: pedir bilhete → PUT directo ao S3 → confirmar.
 *
 * O ficheiro NUNCA passa pela API: o bilhete traz um URL pré-assinado e o
 * browser fala directamente com o armazenamento. Poupa banda e memória do
 * servidor, e é o que permite mandar um vídeo de 100 MB sem derrubar o Node.
 *
 * Usa-se `XMLHttpRequest` e não `fetch` por uma razão só: `fetch` ainda não
 * reporta progresso de UPLOAD em nenhum browser estável. Numa barra de
 * progresso falsa a pessoa carrega outra vez — e fica com dois ficheiros.
 */

import type { Snowflake } from "@greenvoice/shared";
import { api, type FileKind, type UploadTicket } from "./api";

export interface UploadResult {
  fileId: Snowflake;
  url: string;
}

/**
 * Os headers com que o PUT sai.
 *
 * O `Content-Type` NÃO está na assinatura (o URL sai com
 * `SignedHeaders=content-length;host`, o presigner do S3 põe o content-type nos
 * não-assináveis de propósito). Ou seja: é ESTE header que decide com que tipo
 * o objecto fica gravado e, mais tarde, com que tipo é servido. Sem ele o MinIO
 * grava `binary/octet-stream` e o `<video>` do outro lado descarrega em vez de
 * tocar — com a base de dados a jurar que é `video/mp4`.
 *
 * Vêm do bilhete porque o servidor já normalizou o tipo (corta parâmetros, baixa
 * a caixa) e é contra esse valor que o `complete` compara o que ficou gravado.
 * Se um servidor mais antigo não os mandar, derivam-se do `contentType` do mesmo
 * bilhete — mesmo valor, outra volta. O que nunca se faz é cair para `{}`: era
 * assim que um anexo ia parar ao bucket sem tipo nenhum.
 */
export function uploadHeaders(ticket: UploadTicket): Record<string, string> {
  if (ticket.headers && Object.keys(ticket.headers).length > 0) return ticket.headers;
  return ticket.contentType ? { "Content-Type": ticket.contentType } : {};
}

export class UploadError extends Error {
  constructor(
    message: string,
    readonly aborted = false,
  ) {
    super(message);
    this.name = "UploadError";
  }
}

/**
 * Envia o ficheiro e devolve o id já confirmado, pronto a entrar em
 * `attachmentIds`. `onProgress` recebe 0..1.
 */
export function uploadAttachment(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  return runUpload(file, "ATTACHMENT", onProgress, signal);
}

/**
 * O mesmo fluxo para imagens de finalidade fixa (avatar, banner, ícone). O
 * `kind` TEM de bater certo com o do upload: o servidor grava-o no ficheiro e
 * o `claimImage` recusa-o depois se não corresponder — um avatar tem de nascer
 * como AVATAR, não dá para reclassificar um anexo genérico.
 */
export function uploadImage(
  file: File,
  kind: FileKind,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  return runUpload(file, kind, onProgress, signal);
}

async function runUpload(
  file: File,
  kind: FileKind,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<UploadResult> {
  const ticket = await api.files.createUpload({
    filename: file.name,
    // Browsers deixam `type` vazio em extensões que não conhecem. Vai
    // `application/octet-stream`, que a whitelist do servidor recusa com o nome
    // do tipo à frente — é a resposta certa: sem saber o que é, não entra.
    contentType: file.type || "application/octet-stream",
    size: file.size,
    kind,
  });

  await put(ticket, file, onProgress, signal);
  const done = await api.files.complete(ticket.fileId);
  return { fileId: done.id, url: done.url };
}

function put(
  ticket: UploadTicket,
  file: File,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadError("Envio cancelado.", true));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(ticket.method || "PUT", ticket.uploadUrl, true);
    // Porquê estes headers e não os que o browser inventa: ver `uploadHeaders`.
    for (const [header, value] of Object.entries(uploadHeaders(ticket))) {
      xhr.setRequestHeader(header, value);
    }

    // A assinatura já autoriza este PUT; mandar cookies faria o S3 recusar por
    // conflito de credenciais — e enviaria a nossa sessão para fora do domínio.
    xhr.withCredentials = false;

    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      });
    }

    const abort = () => xhr.abort();
    signal?.addEventListener("abort", abort, { once: true });

    const finish = () => signal?.removeEventListener("abort", abort);

    xhr.addEventListener("load", () => {
      finish();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new UploadError(`O armazenamento recusou o arquivo (${xhr.status}).`));
      }
    });
    xhr.addEventListener("error", () => {
      finish();
      reject(new UploadError("Falha de rede durante o envio."));
    });
    xhr.addEventListener("abort", () => {
      finish();
      reject(new UploadError("Envio cancelado.", true));
    });

    xhr.send(file);
  });
}
